import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection, TransactionInstruction } from "@solana/web3.js";
import { createHarvestInstruction } from "@staratlas/factory";
import { ATLAS_MINT } from "@stepfinance/common";
import { FLEET_PROGRAM_ID } from "@stepfinance/dex";
import type { StakingPosition } from "@stepfinance/staking";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { findOrCreateAccountByMint } from "../../../actions/account";
import { createInstructionsToTakeFee } from "../../../actions/fee";
import { confirmOptions } from "../../../constants/transaction";
import type { StakingPositions } from "../../../types/staking";
import { getTokenByMint } from "../../../utils/tokenInfo";
import { handlePreSendTxnError, sendTransaction } from "../../../utils/transactions";
import type { ConnectedWallet } from "../..";
import { useConnectedWallet } from "../..";

export const useClaimShipRewards = () => {
  const queryClient = useQueryClient();
  const { connection } = useConnection();
  const wallet = useConnectedWallet();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    mutationFn: (stakePosition: StakingPositions) => claimShipRewards(connection, wallet!, stakePosition),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["starAtlasShipStaking", wallet?.publicKey.toBase58()] }),
    onError: (error: Error) => handlePreSendTxnError(error, "Error claiming rewards"),
  });
};

const claimShipRewards = async (connection: Connection, wallet: ConnectedWallet, stakePosition: StakingPosition) => {
  const { stakingMint } = stakePosition;
  const atlasInfo = getTokenByMint(ATLAS_MINT.toBase58());

  if (wallet && atlasInfo) {
    const createAccountInstructions: TransactionInstruction[] = [];
    const atlasAccount = await findOrCreateAccountByMint(
      connection,
      wallet.publicKey,
      wallet.publicKey,
      createAccountInstructions,
      ATLAS_MINT,
    );
    const harvestIX = await createHarvestInstruction(
      connection,
      wallet.publicKey,
      ATLAS_MINT,
      stakingMint,
      FLEET_PROGRAM_ID,
    );
    const feeIX = await createInstructionsToTakeFee(
      connection,
      wallet,
      ATLAS_MINT,
      atlasInfo.decimals,
      stakePosition.pendingReward,
      atlasAccount,
    );

    await sendTransaction(
      connection,
      wallet,
      [
        ...createAccountInstructions,
        ...harvestIX.instructions,
        ...feeIX.createAccountInstructions,
        ...feeIX.feeInstructions,
      ],
      [],
      true,
      confirmOptions,
      true,
    );
    // clear pending rewards so UI updates instantly for better UX
    stakePosition.pendingReward = 0;
  }
};
